import { type OrthographicCamera, type Scene } from 'three';
import { watchImmediate } from '@vueuse/core';
import { stopAll, type StopHandle } from '@/util';
import { type SceneRuntime } from '@/lib/threeScene/runtime/createSceneRuntime';
import { applyCameraViewport, updateViewport } from '@/lib/threeScene/updateViewport';
import { updateCamera } from '@/lib/threeScene/camera';
import { type RenderingContext, type RenderingState } from '@/lib/threeScene/renderingState';

export type AttachRenderingStateOptions = {
    runtime: SceneRuntime;
    state: RenderingState;
    scene: Scene;
    camera: OrthographicCamera;
    container: HTMLElement;
    canvas: HTMLCanvasElement;
};

/**
 * Bind a {@link RenderingState} to a scene runtime: reactive clear-color and
 * local-clipping watchers, viewport-driven camera frustum, and the
 * camera-controls + animation loop.
 *
 * Use this inside {@link useScene}'s `setup` for scenes built on the generic
 * `RenderingState` + `updateCamera` system. Returns a StopHandle that should
 * be combined with the rest of the scene's stop handles.
 */
export function attachRenderingState(options: AttachRenderingStateOptions): StopHandle {
    const { runtime, state, scene, camera, container, canvas } = options;

    const context: RenderingContext = {
        container,
        canvas,
        renderer: runtime.renderer,
        scene,
        camera,
        state,
        beforeRender: runtime.beforeRender,
    };

    runtime.onResize((size) => applyCameraViewport(context, size));

    return stopAll(
        watchImmediate(
            () => state.clearColor,
            (color) => runtime.renderer.setClearColor(color),
        ),
        watchImmediate(
            () => state.localClippingEnabled,
            (enabled) => (runtime.renderer.localClippingEnabled = enabled),
        ),
        updateViewport(context),
        updateCamera(context),
    );
}
